import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { SocialFollowBar } from '../components/SocialButtons';
import { Truck, Package, Shield, DollarSign, CheckCircle, ArrowRight, Users, MapPin, Star } from 'lucide-react';
import { toast } from 'sonner';

const JoinPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('shipper');

  const shipperBenefits = [
    { icon: Truck, title: 'Access Verified Carriers', desc: 'Browse pre-approved, insured transporters across Australia' },
    { icon: DollarSign, title: 'Competitive Quotes', desc: 'Receive multiple quotes and choose the best price for your freight' },
    { icon: Shield, title: 'Secure Payments', desc: 'Payment escrow protects your money until the job is done' },
    { icon: MapPin, title: 'Australia-Wide Coverage', desc: 'From Perth to Sydney, Brisbane to Melbourne — we cover every route' },
  ];

  const carrierBenefits = [
    { icon: Package, title: 'Steady Load Supply', desc: 'Access thousands of loads posted every week across Australia' },
    { icon: Users, title: 'Direct Shipper Contact', desc: 'Connect directly with shippers — no middlemen taking your margin' },
    { icon: Star, title: '3 Months Free Trial', desc: 'Use code FREE3 at signup to get 3 months free — no credit card needed' },
    { icon: MapPin, title: 'Find Loads Near You', desc: 'Get notified when loads are posted within 200km of your location' },
  ];

  const benefits = activeTab === 'shipper' ? shipperBenefits : carrierBenefits;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <SocialFollowBar />

      {/* Hero */}
      <div className="bg-[#1e3a5f] text-white py-16 sm:py-24">
        <div className="container mx-auto px-4 text-center max-w-3xl">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4 leading-tight">
            Australia's Growing<br />Freight Matching Platform
          </h1>
          <p className="text-lg sm:text-xl text-blue-200 mb-8">
            Whether you're shipping freight or hauling loads — join thousands of Aussie businesses already using Truckn Send It.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              onClick={() => navigate('/signup?role=shipper')}
              className="bg-red-600 hover:bg-red-700 text-white text-lg px-8 py-6"
              data-testid="join-shipper-btn"
            >
              <Package className="w-5 h-5 mr-2" /> I Need to Ship Freight
            </Button>
            <Button
              onClick={() => navigate('/signup?role=carrier')}
              className="bg-white text-[#1e3a5f] hover:bg-gray-100 text-lg px-8 py-6"
              data-testid="join-carrier-btn"
            >
              <Truck className="w-5 h-5 mr-2" /> I'm a Carrier / Transporter
            </Button>
          </div>
        </div>
      </div>

      {/* Toggle Section */}
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="flex justify-center mb-8">
          <div className="bg-gray-200 rounded-full p-1 flex">
            <button
              onClick={() => setActiveTab('shipper')}
              className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-colors ${
                activeTab === 'shipper' ? 'bg-[#1e3a5f] text-white' : 'text-gray-600 hover:text-gray-800'
              }`}
              data-testid="join-toggle-shipper"
            >
              For Shippers
            </button>
            <button
              onClick={() => setActiveTab('carrier')}
              className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-colors ${
                activeTab === 'carrier' ? 'bg-[#1e3a5f] text-white' : 'text-gray-600 hover:text-gray-800'
              }`}
              data-testid="join-toggle-carrier"
            >
              For Carriers
            </button>
          </div>
        </div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {benefits.map((b, i) => (
            <Card key={i} className="p-6 hover:shadow-md transition-shadow">
              <div className="flex items-start gap-4">
                <div className="bg-red-50 p-3 rounded-lg">
                  <b.icon className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="font-bold text-[#1e3a5f] text-lg mb-1">{b.title}</h3>
                  <p className="text-gray-600 text-sm">{b.desc}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* How It Works */}
        <div className="text-center mb-12">
          <h2 className="text-2xl font-bold text-[#1e3a5f] mb-8">How It Works</h2>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-2">
            {(activeTab === 'shipper' ? [
              { step: '1', text: 'Post your load for free' },
              { step: '2', text: 'Receive quotes from verified carriers' },
              { step: '3', text: 'Accept the best quote and pay securely' },
              { step: '4', text: 'Your freight gets delivered' },
            ] : [
              { step: '1', text: 'Sign up and get 3 months free (code: FREE3)' },
              { step: '2', text: 'Upload your insurance and equipment' },
              { step: '3', text: 'Browse and quote on loads near you' },
              { step: '4', text: 'Win the job, get paid, and grow your business' },
            ]).map((s, i) => (
              <React.Fragment key={i}>
                <div className="flex flex-col items-center">
                  <div className="w-10 h-10 rounded-full bg-red-600 text-white flex items-center justify-center font-bold text-lg mb-2">{s.step}</div>
                  <p className="text-sm text-gray-700 font-medium text-center max-w-[140px]">{s.text}</p>
                </div>
                {i < 3 && <ArrowRight className="w-5 h-5 text-gray-300 hidden sm:block mt-[-20px]" />}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Promo Banner */}
        {activeTab === 'carrier' && (
          <Card className="p-8 bg-gradient-to-r from-red-600 to-red-700 text-white text-center mb-12">
            <h3 className="text-2xl font-bold mb-2">3 Months FREE for New Carriers</h3>
            <p className="text-red-100 mb-4">Use promo code <span className="bg-white text-red-600 px-3 py-1 rounded-full font-bold text-lg">FREE3</span> during signup</p>
            <p className="text-sm text-red-200 mb-4">Limited to the first 100 carriers. No credit card required.</p>
            <Button
              onClick={() => navigate('/signup?role=carrier')}
              className="bg-white text-red-600 hover:bg-gray-100 font-bold px-8 py-3"
              data-testid="join-carrier-promo-btn"
            >
              Sign Up Now — It's Free <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Card>
        )}

        {/* CTA */}
        <div className="text-center">
          <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">Ready to Get Started?</h2>
          <p className="text-gray-600 mb-6">Join Australia's fastest-growing freight matching platform.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              onClick={() => navigate(activeTab === 'shipper' ? '/request-quote' : '/signup?role=carrier')}
              className="bg-red-600 hover:bg-red-700 text-white text-lg px-8 py-5"
              data-testid="join-cta-btn"
            >
              {activeTab === 'shipper' ? 'Post a Load Now' : 'Sign Up as Carrier'} <ArrowRight className="w-5 h-5 ml-1" />
            </Button>
            <Button
              onClick={() => navigate('/browse-loads')}
              variant="outline"
              className="text-lg px-8 py-5"
            >
              Browse Available Loads
            </Button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default JoinPage;
